/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { TextField, CircularProgress } from '@material-ui/core';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { showNotification } from '../../App';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import gql from 'graphql-tag';
import { FetchPolicy } from 'apollo-client';
import { client } from '../..';
import { EmailTemplateMessageTypeEnum, EmailTemplateTypeEnum } from '../EmailTemplateRespository';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        width: '100%'
    },
  })
);

interface AvailableAreaOfLawSelectorProps {
    emailType: EmailTemplateTypeEnum;
    disabled?: boolean;
    required?: boolean;
    multiple?: boolean;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem | any; // value only available in single select
    error?: boolean;
    onSelection?: (value: IAutoCompleteItem | IAutoCompleteItem[], name: string) => void;
    className?: string;
    helperText?: string;
    name?: string;
    label?: string;
    disablePortal?: boolean;
    disableCloseOnSelect?: boolean;
    messageType?: EmailTemplateMessageTypeEnum;
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
}

interface AvailableAreaOfLawSelectorState {
    isLoading: boolean;
    areaOfLaw: IAutoCompleteItem[];
}

export const AvailableAreaOfLawSelector: React.FC<AvailableAreaOfLawSelectorProps> = props => {

    const classes = useStyles();

    const [state, setState] = useState<AvailableAreaOfLawSelectorState>({
        isLoading: false,
        areaOfLaw: [],
    });

    useEffect(() => {   
        fetchData();
    // tslint:disable-next-line: align
    }, [props.messageType]);

    const fetchData = () => {

        setState({
            ...state,
            isLoading: true
        });

        let availableAreaOfLawQueryParams: AvailableAreaOfLawQueryParams = {
            emailType: EmailTemplateTypeEnum[props.emailType],
            areaOfLaw: props.value && props.value.label ? props.value.label : '',
            messageType: props.messageType
        };

        retrieveAvailableAreaOfLawData(
            availableAreaOfLawQueryParams,
            true, // Since we are updating and creating, we do not fetch cached data
            // tslint:disable-next-line: no-console
            (data) => onRetrieveData(data),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');
            }
        );
    };

    const onRetrieveData = (data: AreaOfLawData) => {
        setState({
            ...state,
            areaOfLaw: data.lookup.availableAreaOfLaw.map( (source: AreaOfLaw) => ({value: source.guidId, label: source.description })),
            isLoading: false
        });
    };

    // tslint:disable-next-line: no-any
    const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
        // tslint:disable-next-line: no-console
        console.log('onChange', value);
        if (props.onSelection) {
            props.onSelection(value, props.name ? props.name : 'areaOfLaw');
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    };

    return (
        <div className={`${classes.root} ${props.className}`}>
            { props.multiple ? 
                (
                    <Autocomplete
                        value={props.value ? props.value : []}
                        multiple={true}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.areaOfLaw}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}       
                        getOptionSelected={(option, value) => value.value === option.value}      
                        size={props.size ? props.size : 'medium'} 
                        ChipProps={{
                            size: props.size ? props.size : 'medium'
                        }}    
                        onBlur={props.onBlur}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                
                                label={props.label ? props.label : 'Area of Law'}
                                variant={props.variant ? props.variant : 'standard'} 
                                fullWidth={true} 
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}      
                                helperText={props.helperText}      
                                size={props.size ? props.size : 'medium'}     
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                            {part.text}
                                        </span>
                                    ))}
                                </div>
                            );
                        }}
                    />
                )
                :
                (
                    <Autocomplete
                        value={props.value ? props.value : null}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.areaOfLaw}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}    
                        getOptionSelected={(option, value) => value.value === option.value}     
                        // tslint:disable-next-line: jsx-no-lambda
                        ChipProps={{
                            size: props.size ? props.size : 'medium'
                        }}   
                        onBlur={props.onBlur} 
                        size={props.size ? props.size : 'medium'}
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                label={props.label ? props.label : 'Area of Law'}
                                variant={props.variant ? props.variant : 'standard'} 
                                fullWidth={true} 
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}         
                                size={props.size ? props.size : 'medium'}   
                                helperText={props.helperText}                
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                {parts.map((part, index) => (
                                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                    {part.text}
                                    </span>
                                ))}
                                </div>
                            );
                        }}
                    />
                )
            }
        </div>
    );
};

export interface AvailableAreaOfLawQueryParams {
    emailType: string | null;
    areaOfLaw: string | null;
    messageType?: EmailTemplateMessageTypeEnum;
}

export function retrieveAvailableAreaOfLawData(
    query: AvailableAreaOfLawQueryParams,
    refreshData: boolean,
    onSuccess: (data: AreaOfLawData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void
): void {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query({
            query: AvailableAreaOfLawQuery,
            variables: {
                emailType: query.emailType,
                areaOfLaw: query.areaOfLaw,
                messageType: query.messageType,
            },
            fetchPolicy: fetchPolicy,
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

export interface AreaOfLawData {
    loading?: boolean;
    networkStatus?: number;
    lookup: Lookup;
}

export interface Lookup {
    availableAreaOfLaw: AreaOfLaw[];
}

export interface AreaOfLaw {
    guidId:      string;
    description: string;
}

const AvailableAreaOfLawQuery = gql`
    query availableAreaOfLaw($emailType: EmailType, $areaOfLaw: String, $messageType: MessageType) {
        lookup{
            availableAreaOfLaw(emailType: $emailType, areaOfLaw: $areaOfLaw, messageType: $messageType) {
                id
                guidId,
                description,
                sequence
            }
        }
    }
`;
